import type { Employee, EmployeeMonthlyInfo } from '../../types/employee';
import {
  isBusinessServicePay,
  isDailyWorkerPay,
  isRegularEmployee,
  resolvePayType,
  type EmployeePayType,
} from '../../types/employee';
import { PENSION_HEALTH_MIN_WORK_DAYS } from './daily-worker-insurance';
import { netPayFromMonthlyInfo } from './net-pay';

export function netPay(info: EmployeeMonthlyInfo): number {
  return netPayFromMonthlyInfo(info);
}

export function deductionLabel(
  employee: Employee,
  info: EmployeeMonthlyInfo,
  workDays: number | null,
): string {
  if (isBusinessServicePay(employee)) return '원천징수(3.3%)';

  if (isDailyWorkerPay(employee)) {
    if (info.totalEmployeeInsurance <= 0) {
      return info.withholdingTax > 0 || info.localTax > 0
        ? '원천징수'
        : '고용보험(근로자)';
    }
    if (employee.hasFourInsurance && (workDays ?? 0) >= PENSION_HEALTH_MIN_WORK_DAYS) {
      return '4대보험';
    }
    return '고용보험(근로자)';
  }

  return info.hasInsurance ? '4대보험' : '원천징수(3.3%)';
}

export interface PayTypeSection {
  key: EmployeePayType | 'regular';
  label: string;
  employees: Employee[];
}

const PAY_TYPE_SECTION_ORDER: Array<{ key: EmployeePayType | 'regular'; label: string; match: (e: Employee) => boolean }> = [
  { key: 'regular', label: '월급제', match: isRegularEmployee },
  { key: 'businessService', label: '용역', match: isBusinessServicePay },
  { key: 'dailyWorker', label: '일당', match: isDailyWorkerPay },
];

export function groupEmployeesByPayType(employees: Employee[]): PayTypeSection[] {
  return PAY_TYPE_SECTION_ORDER.map(({ key, label, match }) => ({
    key,
    label,
    employees: employees.filter(match),
  })).filter((section) => section.employees.length > 0);
}

export function payTypeLabel(payType: EmployeePayType): string {
  switch (payType) {
    case 'monthly':
      return '월급제';
    case 'hourly':
      return '시급제';
    case 'dailyWorker':
      return '일당';
    case 'businessService':
      return '용역';
    default:
      return '월급제';
  }
}

export function payTypeChipLabel(employee: Employee): string {
  const pt = resolvePayType(employee.payType, employee.isHourly);
  if (pt === 'dailyWorker') return '일당';
  if (pt === 'businessService') return '용역';
  if (pt === 'hourly') return '시급';
  return '월급';
}
